import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { CheckCircle, Paperclip, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import communicationService from '../../services/communicationService';
import LockedFeature from '../LockedFeature';
import './ChatWindow.css';
import MessageInput from './MessageInput';

/**
 * ChatWindow Component - Phase 3a Enhanced Integration
 * 
 * Main chat window displaying message thread with student context and threading
 * Handles message display, marking as read, and delegates input to MessageInput component
 * 
 * This component focuses on MESSAGE DISPLAY and THREADING
 * The MessageInput component handles all message COMPOSITION and SENDING
 * 
 * State Flow:
 * 1. ParentTeacherChat manages global state (conversations, selectedConversation, messages)
 * 2. ChatWindow receives messages array as prop
 * 3. ChatWindow groups messages by student_id into threads
 * 4. MessageInput (child) sends message → triggers ParentTeacherChat.handleSendMessage
 * 5. ParentTeacherChat updates messages array → re-renders ChatWindow with new messages
 * 6. ChatWindow re-groups and displays new threading
 * 
 * @component
 * @param {string} conversationId - ID of active conversation
 * @param {Array} messages - Array of message objects with all needed fields
 * @param {Function} onSendMessage - Callback when message is sent ({content, student_id, message_type})
 * @param {Function} onMarkAsRead - Callback when message is hovered (messageId)
 * @param {boolean} isLoading - Loading state for initial message fetch
 * @param {boolean} isSending - Sending state (passed to MessageInput for button disable)
 * @param {string} userRole - Current user's role ('Parent', 'Educator', etc.)
 * @param {Array} students - Array of student objects for message threading context
 * @returns {JSX.Element} Rendered chat window with threading
 * 
 * Features:
 * - Messages grouped by student_id into collapsible threads
 * - Thread headers with unread count and message type indicators
 * - Smart date/time formatting (Today HH:MM, Yesterday, Date)
 * - Read status tracking (✓✓ indicators)
 * - Message type color coding (General, Grade, Attendance, Behavior, Fee, Urgent)
 * - Auto-mark-as-read on hover
 * - Graceful empty states
 * - Loading indicators
 * - Smooth thread toggle animations
 * 
 * Performance:
 * - useCallback prevents unnecessary re-renders of child components
 * - useMemo efficiently groups messages only when messages array changes
 * - Thread state isolated to this component (doesn't affect parent)
 * 
 * Integration Points:
 * - receives messages[] from ParentTeacherChat
 * - calls onSendMessage(messageData) via MessageInput child
 * - calls onMarkAsRead(messageId) on message hover
 */
const ChatWindow = ({ 
  conversationId, 
  messages = [],
  onSendMessage, 
  onMarkAsRead, 
  isLoading = false, 
  isSending = false,
  userRole = 'Educator',
  students = []
}) => {
  const [expandedThreads, setExpandedThreads] = useState(new Set());

  /**
   * Auto-expand thread when messages load
   * For parents and teachers: auto-expand the unified chat
   * For educators: auto-expand first student thread
   */
  useEffect(() => {
    if (messages && messages.length > 0) {
      if (userRole === 'parent' || userRole === 'teacher' || userRole === 'staff') {
        setExpandedThreads(new Set(['unified']));
      } else {
        const firstStudentId = messages[0].student_id || 'general';
        setExpandedThreads(new Set([firstStudentId]));
      }
    } else {
      setExpandedThreads(new Set());
    }
  }, [messages, conversationId, userRole]);

  /**
   * Memoized message grouping
   * For parent-teacher/teacher-parent chats: show all messages in single unified thread (no grouping)
   * For multi-recipient chats: group by student_id
   */
  const groupedMessages = useMemo(() => {
    // For parent and teacher chats, show all messages in one unified window
    if (userRole === 'parent' || userRole === 'teacher' || userRole === 'staff') {
      return [{
        studentId: 'unified',
        studentName: 'Conversation',
        messages: messages,
        unreadCount: messages.filter(m => !m.is_read).length,
        lastMessageTime: messages.length > 0 ? messages[messages.length - 1].created_at : null,
        messageTypes: new Set(messages.map(m => m.message_type).filter(Boolean))
      }];
    }

    // For educators, group by student_id
    const groups = {};
    
    messages.forEach(msg => {
      const studentId = msg.student_id || 'general';
      const studentName = msg.student_name || students.find(s => s.id === studentId)?.name || 'General Discussion';
      
      if (!groups[studentId]) {
        groups[studentId] = {
          studentId,
          studentName,
          messages: [],
          unreadCount: 0,
          lastMessageTime: null,
          messageTypes: new Set()
        };
      }
      
      groups[studentId].messages.push(msg);
      groups[studentId].lastMessageTime = msg.created_at;
      if (!msg.is_read) groups[studentId].unreadCount++;
      if (msg.message_type) groups[studentId].messageTypes.add(msg.message_type);
    });

    // Sort by last message time (newest first)
    return Object.values(groups).sort((a, b) => 
      new Date(b.lastMessageTime) - new Date(a.lastMessageTime)
    );
  }, [messages, students, userRole]);

  /**
   * Toggle thread expansion state
   */
  const toggleThread = useCallback((studentId) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }, []);

  /**
   * Format message timestamp with smart display
   * Shows: Today HH:MM, Yesterday HH:MM, or Date (short format)
   */
  const formatMessageTime = useCallback((dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }, []);

  /**
   * Download a message attachment — same blob/URL.createObjectURL/link.click
   * pattern as PayrollPage.js's handleDownloadSsnitExport.
   */
  const handleDownloadAttachment = useCallback(async (attachment) => {
    try {
      const response = await communicationService.downloadAttachment(attachment.id);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_filename || 'attachment';
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error(err.message || 'Failed to download attachment');
    }
  }, []);

  /**
   * Get color for message type badge
   */
  const getMessageTypeColor = useCallback((type) => {
    const colors = {
      'general': '#6B7280',
      'grade': '#EF4444',
      'attendance': '#F59E0B',
      'behavior': '#8B5CF6',
      'fee': '#10B981',
      'urgent': '#DC2626'
    };
    return colors[type] || '#6B7280';
  }, []);

  /**
   * Get gradient color for avatar based on name
   */
  const getGradientColor = useCallback((name) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    ];
    const hash = name?.charCodeAt(0) || 0;
    return colors[hash % colors.length];
  }, []);

  return (
    <div className="chat-window">
      {/* Message Thread Display */}
      <div className="message-thread">
        {isLoading ? (
          // Loading State
          <div className="thread-loading">
            <div className="mini-spinner"></div>
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          // Empty State
          <div className="thread-empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          // Messages Grouped by Student Thread
          <div className="message-groups">
            {groupedMessages.map(group => (
              <div key={group.studentId} className="message-group">
                {/* Thread Header - Click to Expand/Collapse */}
                <div 
                  className="thread-header"
                  onClick={() => toggleThread(group.studentId)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={expandedThreads.has(group.studentId)}
                  aria-label={`Toggle messages for ${group.studentName}`}
                >
                  <div className="thread-info">
                    {/* Student Avatar */}
                    <div 
                      className="student-badge"
                      style={{ background: getGradientColor(group.studentName) }}
                      title={group.studentName}
                    >
                      <span className="student-initial">
                        {group.studentName.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* Student Name and Message Count */}
                    <div className="thread-details">
                      <h4 className="student-name">{group.studentName}</h4>
                      <span className="message-count">
                        {group.messages.length} message{group.messages.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  {/* Thread Metadata */}
                  <div className="thread-meta">
                    {/* Unread Count Badge */}
                    {group.unreadCount > 0 && (
                      <span className="thread-unread-badge" title={`${group.unreadCount} unread`}>
                        {group.unreadCount}
                      </span>
                    )}

                    {/* Message Type Indicators */}
                    {group.messageTypes.size > 0 && (
                      <div className="thread-type-indicators">
                        {Array.from(group.messageTypes).slice(0, 3).map(type => (
                          <span 
                            key={type}
                            className="type-indicator"
                            style={{ backgroundColor: getMessageTypeColor(type) }}
                            title={type}
                          >
                            {type.charAt(0).toUpperCase()}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Expand/Collapse Arrow */}
                    <span 
                      className={`thread-toggle ${expandedThreads.has(group.studentId) ? 'expanded' : ''}`}
                      aria-hidden="true"
                    >
                      ▶
                    </span>
                  </div>
                </div>

                {/* Messages Container - Expanded on Click */}
                {expandedThreads.has(group.studentId) && (
                  <div className="thread-messages">
                    {group.messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`message ${msg.sender_id === conversationId ? 'message-received' : 'message-sent'}`}
                        onMouseEnter={() => !msg.is_read && onMarkAsRead(msg.id)}
                      >
                        {/* Message Avatar */}
                        <div 
                          className="message-avatar"
                          style={{ background: getGradientColor(msg.sender_name) }}
                          title={msg.sender_name}
                        >
                          {msg.sender_name.charAt(0).toUpperCase()}
                        </div>

                        {/* Message Content */}
                        <div className="message-content">
                          {/* Message Header: Sender, Type, Time */}
                          <div className="message-header">
                            <span className="message-sender">{msg.sender_name}</span>
                            
                            {/* Message Type Badge */}
                            {msg.message_type && msg.message_type !== 'general' && (
                              <span 
                                className="message-type-badge"
                                style={{ backgroundColor: getMessageTypeColor(msg.message_type) }}
                                title={msg.message_type}
                              >
                                {msg.message_type}
                              </span>
                            )}

                            <span className="message-time" title={msg.created_at}>
                              {formatMessageTime(msg.created_at)}
                            </span>

                            {/* Read Status Indicator */}
                            {msg.is_read && (
                              <span className="message-read-indicator" title="Read"><CheckCircle size={14} weight="fill" /><CheckCircle size={14} weight="fill" /></span>
                            )}
                          </div>

                          {/* Message Subject (if available) */}
                          {msg.subject && msg.subject !== 'Message' && (
                            <div className="message-subject">{msg.subject}</div>
                          )}

                          {/* Message Text Content */}
                          <p className="message-text">{msg.content}</p>

                          {/* Attachments */}
                          {msg.attachments?.length > 0 && (
                            <div className="message-attachments">
                              {msg.attachments.map(attachment => (
                                <LockedFeature key={attachment.id} module="comms_premium">
                                  <button
                                    type="button"
                                    className="attachment-chip"
                                    onClick={() => handleDownloadAttachment(attachment)}
                                    title={`Download ${attachment.original_filename}`}
                                  >
                                    <Paperclip size={13} />
                                    <span className="attachment-chip-name">{attachment.original_filename}</span>
                                    <DownloadSimple size={13} />
                                  </button>
                                </LockedFeature>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Input Component - Standalone for Composition */}
      <MessageInput
        onSendMessage={onSendMessage}
        students={students}
        isSending={isSending}
        userRole={userRole}
      />
    </div>
  );
};

export default ChatWindow;
